/**
 * Company Branding Types
 */

import { formatHouseNumber } from '@/utils/addressUtils';

/**
 * Structured address for company profiles
 * Same structure as used in customer addresses for consistency
 */
export type CompanyAddress = {
  street?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  lat?: number;
  lng?: number;
  formattedAddress?: string;
};

/**
 * Bank account information for a company profile
 * Used for payment details on invoices
 */
export type BankAccount = {
  iban?: string;
  bic?: string;
  accountName?: string;
};

/**
 * Company Profile - Represents a single company/BV
 * Teams can have multiple company profiles for different legal entities
 *
 * SINGLE SOURCE OF TRUTH for:
 * - Logo (logoUrl)
 * - Bank account details (bankAccount)
 * - Company info (name, VAT, KvK, address)
 *
 * Templates should NOT store logo or bank info - they reference the company profile.
 */
export type CompanyProfile = {
  id: string;
  name: string;
  vatNumber?: string;
  kvkNumber?: string;
  /** @deprecated Use addressData instead. Kept for backward compatibility with existing profiles. */
  address?: string;
  /** Structured address data from Google Places */
  addressData?: CompanyAddress;
  logoUrl?: string;
  /** Bank account details for payment info on invoices */
  bankAccount?: BankAccount;
  isDefault: boolean;
  createdAt?: Date;
  updatedAt?: Date;
};

/**
 * Company Profiles Settings - Stored in Firestore
 * Path: teams/{teamId}/settings/companyProfiles
 */
export type CompanyProfilesSettings = {
  profiles: CompanyProfile[];
  updatedAt?: Date;
};

/**
 * @deprecated Use CompanyProfile instead
 * Legacy single-company branding type, kept for backward compatibility
 */
export type CompanyBranding = {
  logoUrl?: string;
  companyName?: string;
  vatNumber?: string;
  kvkNumber?: string;
  address?: string;
  updatedAt?: Date;
  version?: number;
};

/**
 * Convert legacy CompanyBranding to a CompanyProfile
 */
export function migrateToCompanyProfile(branding: CompanyBranding, id: string): CompanyProfile {
  return {
    id,
    name: branding.companyName || '',
    vatNumber: branding.vatNumber,
    kvkNumber: branding.kvkNumber,
    address: branding.address,
    logoUrl: branding.logoUrl,
    isDefault: true,
    updatedAt: branding.updatedAt,
  };
}

/**
 * Convert CompanyProfile to legacy CompanyBranding format
 * Used for backward compatibility with existing PDF rendering
 */
export function profileToBranding(profile: CompanyProfile): CompanyBranding {
  return {
    logoUrl: profile.logoUrl,
    companyName: profile.name,
    vatNumber: profile.vatNumber,
    kvkNumber: profile.kvkNumber,
    address: profile.address,
    updatedAt: profile.updatedAt,
  };
}

export type BrandingStatus = 'idle' | 'hydrated' | 'listening' | 'error';
export type BrandingSource = 'cache' | 'live';

/**
 * Format a CompanyAddress to a display string
 * Falls back to legacy address string if addressData is not available
 */
export function formatCompanyAddress(profile: CompanyProfile): string {
  // Use structured address if available
  if (profile.addressData) {
    const { street, houseNumber, houseNumberSuffix, postalCode, city, country } =
      profile.addressData;
    const parts: string[] = [];

    // Street + house number + suffix
    if (street || houseNumber || houseNumberSuffix) {
      const formattedHouseNumber = formatHouseNumber(houseNumber, houseNumberSuffix);
      const streetLine =
        street && formattedHouseNumber
          ? `${street} ${formattedHouseNumber}`
          : street || formattedHouseNumber || '';
      if (streetLine) {
        parts.push(streetLine);
      }
    }

    // Postal code + city
    if (postalCode || city) {
      parts.push([postalCode, city].filter(Boolean).join(' '));
    }

    // Country (optional, often omitted for domestic)
    if (country && country !== 'Nederland' && country !== 'Netherlands' && country !== 'NL') {
      parts.push(country);
    }

    return parts.join(', ');
  }

  // Fall back to legacy string address
  return profile.address || '';
}

/**
 * Get the address string for a company profile (for display/PDF)
 * Returns formatted structured address or legacy string
 */
export function getCompanyAddressString(profile: CompanyProfile): string {
  return formatCompanyAddress(profile);
}
